import type { ReactNode } from "react";
import {
  Box,
  Button,
  IconButton,
  Stack,
  Typography,
  type ButtonProps,
} from "@mui/material";
import { Close, SellOutlined } from "@mui/icons-material";
import type { SxProps, Theme } from "@mui/material/styles";
import {
  BANNER_VARIANTS,
  type BannerIconTone,
  type BannerVariant,
  type InlineBannerVariant,
} from "./bannerVariants";

type BannerPartSize = "small" | "medium" | "large";

const TILE_SIZES: Record<
  BannerPartSize,
  { tile: number; icon: number; radius: number }
> = {
  small: { tile: 40, icon: 24, radius: 2 },
  medium: { tile: 48, icon: 24, radius: 2 },
  large: { tile: 64, icon: 40, radius: 3 },
};

const TILE_BACKGROUNDS: Record<BannerIconTone, string> = {
  light: "action.selected",
  color: "banner.promoIconBackground",
  dark: "banner.darkIconBackground",
};

type BannerIconTileProps = {
  icon?: ReactNode;
  tone: BannerIconTone;
  size: BannerPartSize;
};

/**
 * The banner's icon visual. MUI icons take the tile's grey or white; icons
 * that carry their own colours (Proxy Type icons, brand logos) keep them.
 */
export const BannerIconTile = ({ icon, tone, size }: BannerIconTileProps) => {
  const { tile, icon: iconSize, radius } = TILE_SIZES[size];

  return (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="center"
      flexShrink={0}
      width={tile}
      height={tile}
      borderRadius={radius}
      bgcolor={TILE_BACKGROUNDS[tone]}
      color={tone === "light" ? "action.active" : "common.white"}
      sx={{ "& svg": { fontSize: iconSize } }}
    >
      {icon ?? <SellOutlined />}
    </Box>
  );
};

type BannerImageProps = {
  src: string;
  alt: string;
  width: number | string;
  height: number;
};

export const BannerImage = ({ src, alt, width, height }: BannerImageProps) => (
  <Box
    component="img"
    src={src}
    alt={alt}
    display="block"
    flexShrink={0}
    width={width}
    height={height}
    sx={{ objectFit: "cover" }}
  />
);

const TEXT_SIZES: Record<
  BannerPartSize,
  { titleVariant: "body1" | "h6"; spacing: number }
> = {
  small: { titleVariant: "body1", spacing: 0.5 },
  medium: { titleVariant: "h6", spacing: 1 },
  large: { titleVariant: "h6", spacing: 2 },
};

type BannerTextProps = {
  title: string;
  text?: string;
  variant: BannerVariant | InlineBannerVariant;
  size: BannerPartSize;
  align?: "left" | "center";
  titleId?: string;
};

/** Title and optional text, sized to match the icon tile of the same size. */
export const BannerText = ({
  title,
  text,
  variant,
  size,
  align,
  titleId,
}: BannerTextProps) => {
  const tokens = BANNER_VARIANTS[variant];
  const { titleVariant, spacing } = TEXT_SIZES[size];

  return (
    <Stack spacing={spacing} flex={1} minWidth={0} textAlign={align}>
      <Typography
        id={titleId}
        variant={titleVariant}
        fontWeight={titleVariant === "body1" ? "bold" : undefined}
        color={tokens.titleColor}
      >
        {title}
      </Typography>
      {text && (
        <Typography variant="body2" color={tokens.textColor}>
          {text}
        </Typography>
      )}
    </Stack>
  );
};

type BannerCtaProps = {
  label: string;
  onClick: () => void;
  variant: BannerVariant | InlineBannerVariant;
  sx?: SxProps<Theme>;
} & Pick<ButtonProps, "size" | "fullWidth" | "endIcon">;

export const BannerCta = ({
  label,
  onClick,
  variant,
  sx,
  ...rest
}: BannerCtaProps) => (
  <Button
    variant="contained"
    color="primary"
    onClick={onClick}
    data-testid="banner-cta"
    sx={[
      { flexShrink: 0, fontWeight: 600 },
      BANNER_VARIANTS[variant].invertedCta && {
        bgcolor: "banner.ctaBackground",
        color: "common.black",
        "&:hover": { bgcolor: "banner.ctaHoverBackground" },
      },
      ...(Array.isArray(sx) ? sx : [sx]),
    ]}
    {...rest}
  >
    {label}
  </Button>
);

type BannerCloseButtonProps = {
  onClose: () => void;
  variant: BannerVariant | InlineBannerVariant;
  sx?: SxProps<Theme>;
};

export const BannerCloseButton = ({
  onClose,
  variant,
  sx,
}: BannerCloseButtonProps) => (
  <IconButton
    size="small"
    aria-label="Dismiss"
    onClick={onClose}
    data-testid="banner-close"
    sx={[
      { flexShrink: 0, p: 1, color: BANNER_VARIANTS[variant].closeColor },
      ...(Array.isArray(sx) ? sx : [sx]),
    ]}
  >
    <Close sx={{ fontSize: 16 }} />
  </IconButton>
);
